// API Service for MeraGym Trainer Dashboard
import axios from 'axios';
import config from '../config';

// Create axios instance with default config
const api = axios.create({
    baseURL: config.apiUrl,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// ==========================================
// AUTHENTICATION APIs
// ==========================================

export const authAPI = {
    // Check if email exists
    checkEmail: (email) => {
        return api.post('/check-trainer-email', { email });
    },

    // Send OTP
    sendOTP: (email) => {
        return api.post('/send-otp', { email });
    },

    // Verify OTP
    verifyOTP: (email, otp) => {
        return api.post('/verify-otp', { email, otp });
    },

    // Register trainer
    register: (data) => {
        return api.post('/trainer-register', data);
    },

    // Login
    login: (email, password) => {
        return api.post('/trainer-login', { email, password });
    },

    // Logout
    logout: () => {
        return api.post('/logout');
    },

    // Refresh token
    refreshToken: () => {
        return api.post('/refresh-token');
    },
};

// ==========================================
// PROFILE APIs
// ==========================================

export const profileAPI = {
    // Get trainer profile
    getProfile: () => {
        return api.get('/trainers/profile');
    },

    // Update basic info
    updateBasicInfo: (data) => {
        return api.post('/trainers/profile/update', data);
    },

    // Update professional details
    updateProfessional: (data) => {
        return api.put('/trainer/professional', data);
    },

    // Update services
    updateServices: (data) => {
        return api.put('/trainer/services', data);
    },

    // Update availability
    updateAvailability: (data) => {
        return api.put('/trainer/availability', data);
    },

    // Update media
    updateMedia: (data) => {
        return api.put('/trainer/media', data);
    },

    // Get profile completion percentage
    getCompletionPercentage: () => {
        return api.get('/trainer/completion-percentage');
    },

    // Toggle profile visibility
    toggleVisibility: (isVisible) => {
        return api.post('/trainers/profile/visibility', { is_visible: isVisible });
    },
};

// ==========================================
// PROFESSIONAL APIs
// ==========================================

export const professionalAPI = {
    // Get professional details
    getProfessional: () => {
        return api.get('/trainers/professional');
    },

    // Update professional details
    updateProfessional: (data) => {
        return api.post('/trainers/professional/update', data);
    },

    // Delete certification
    deleteCertification: (certificationId) => {
        return api.post('/trainers/professional/delete-certification', {
            certification_id: certificationId,
        });
    },

    // Get all skills
    getSkills: () => {
        return api.get('/trainer-skill');
    },
};

// ==========================================
// CERTIFICATION APIs
// ==========================================

export const certificationAPI = {
    // Get all certifications
    getAll: () => {
        return api.get('/trainer/certifications');
    },

    // Add certification
    add: (data) => {
        return api.post('/trainer/certifications', data);
    },

    // Update certification
    update: (id, data) => {
        return api.put(`/trainer/certifications/${id}`, data);
    },

    // Delete certification
    delete: (id) => {
        return api.delete(`/trainer/certifications/${id}`);
    },
};

// ==========================================
// SERVICE APIs
// ==========================================

export const serviceAPI = {
    // Get all services
    getAll: () => {
        return api.get('/trainers/services');
    },

    // Update all services
    updateAll: (data) => {
        return api.post('/trainers/services/update', data);
    },

    // Add service
    add: (data) => {
        return api.post('/trainers/services/add', data);
    },

    // Update single service
    update: (data) => {
        return api.post('/trainers/services/update-single', data);
    },

    // Delete service
    delete: (serviceId) => {
        return api.post('/trainers/services/delete', { service_id: serviceId });
    },
};

// ==========================================
// AVAILABILITY APIs
// ==========================================

export const availabilityAPI = {
    // Get availability
    getAvailability: () => {
        return api.get('/trainers/availability');
    },

    // Update availability
    updateAvailability: (data) => {
        return api.post('/trainers/availability/update', data);
    },

    // Delete time slot
    deleteTimeSlot: (slotId) => {
        return api.post('/trainers/availability/delete-slot', { slot_id: slotId });
    },
};

// ==========================================
// SETTINGS APIs
// ==========================================

export const settingsAPI = {
    // Get settings
    getSettings: () => {
        return api.get('/trainers/settings');
    },

    // Update notification settings
    updateNotifications: (data) => {
        return api.post('/trainers/settings/notifications', data);
    },

    // Update privacy settings
    updatePrivacy: (data) => {
        return api.post('/trainers/settings/privacy', data);
    },

    // Change password
    changePassword: (data) => {
        return api.post('/trainers/settings/change-password', data);
    },

    // Delete account
    deleteAccount: (password) => {
        return api.post('/trainers/settings/delete-account', { password });
    },
};

// ==========================================
// MEDIA APIs
// ==========================================

export const mediaAPI = {
    // Get media data
    getMedia: () => {
        return api.get('/trainers/media');
    },

    // Update all media data
    updateMedia: (data) => {
        return api.post('/trainers/media/update', data);
    },

    // Upload profile photo
    uploadProfilePhoto: (file) => {
        const formData = new FormData();
        formData.append('photo', file);
        return api.post('/trainers/media/upload-profile-photo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },

    // Upload cover photo
    uploadCoverPhoto: (file) => {
        const formData = new FormData();
        formData.append('coverPhoto', file);
        return api.post('/trainers/media/upload-cover-photo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },

    // Upload gallery photo
    uploadGalleryPhoto: (file) => {
        const formData = new FormData();
        formData.append('photo', file);
        return api.post('/trainers/media/upload-gallery-photo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },

    // Delete gallery photo
    deleteGalleryPhoto: (photoId) => {
        return api.post('/trainers/media/delete-gallery-photo', { photoId });
    },

    // Add video
    addVideo: (url, platform) => {
        return api.post('/trainers/media/add-video', { url, platform });
    },

    // Delete video
    deleteVideo: (videoId) => {
        return api.post('/trainers/media/delete-video', { videoId });
    },
};

// ==========================================
// CLIENT APIs
// ==========================================

export const clientAPI = {
    // Get all clients
    getAll: (params) => {
        return api.get('/trainer/clients', { params });
    },

    // Get client details
    getDetails: (id) => {
        return api.get(`/trainer/clients/${id}`);
    },

    // Update client status
    updateStatus: (id, status) => {
        return api.put(`/trainer/clients/${id}/status`, { status });
    },

    // Add client note
    addNote: (id, note) => {
        return api.post(`/trainer/clients/${id}/notes`, { note });
    },
};

// ==========================================
// LEAD APIs
// ==========================================

export const leadAPI = {
    // Get all leads
    getAll: (params) => {
        return api.get('/trainer/leads', { params });
    },

    // Get lead details
    getDetails: (id) => {
        return api.get(`/trainer/leads/${id}`);
    },

    // Update lead status
    updateStatus: (id, status) => {
        return api.put(`/trainer/leads/${id}/status`, { status });
    },

    // Convert lead to client
    convertToClient: (id) => {
        return api.post(`/trainer/leads/${id}/convert`);
    },
};

// ==========================================
// REVIEW APIs
// ==========================================

export const reviewAPI = {
    // Get all reviews
    getAll: (params) => {
        return api.get('/trainer/reviews', { params });
    },

    // Get review statistics
    getStats: () => {
        return api.get('/trainer/reviews/stats');
    },

    // Respond to review
    respond: (id, response) => {
        return api.post(`/trainer/reviews/${id}/respond`, { response });
    },
};

// ==========================================
// DASHBOARD APIs
// ==========================================

export const dashboardAPI = {
    // Get dashboard data (stats + user profile)
    getDashboard: () => {
        return api.get('/trainers/dashboard');
    },

    // Get dashboard stats
    getStats: () => {
        return api.get('/trainer/dashboard/stats');
    },

    // Get recent activities
    getRecentActivities: () => {
        return api.get('/trainer/dashboard/activities');
    },

    // Get analytics
    getAnalytics: (period) => {
        return api.get('/trainer/dashboard/analytics', { params: { period } });
    },
};

// ==========================================
// PUBLIC APIs (No Auth Required)
// ==========================================

export const publicAPI = {
    // Get trainer by slug
    getTrainerBySlug: (slug) => {
        return axios.get(`${config.apiUrl}/trainer/${slug}`);
    },

    // Get all trainers
    getAllTrainers: (params) => {
        return axios.get(`${config.apiUrl}/trainers`, { params });
    },

    // Search trainers
    searchTrainers: (query, filters) => {
        return axios.get(`${config.apiUrl}/trainers/search`, {
            params: { q: query, ...filters },
        });
    },
};

// ==========================================
// ENQUIRY APIs
// ==========================================

export const enquiryAPI = {
    // Get all enquiries
    getAll: () => {
        return api.get('/trainers/consultations');
    },

    // Create new enquiry
    create: (data) => {
        return api.post('/trainer-consultation', data);
    },

    // Mark enquiry as viewed
    markAsViewed: (id) => {
        return api.put(`/trainers/consultations/${id}/view`);
    },
};

// Export default api instance for custom calls
export default api;
